import type * as api from '@gitbook/api-client';
import { Inline } from '@gitbook/slate';

import { DEFAULT_INLINE_BUTTON_SIZE, INLINE_BUTTON, type InlineButtonData } from '../types';

type DocumentActionParameters = {
    [Kind in api.DocumentAction['action']]: ReadonlyArray<
        Exclude<keyof Extract<api.DocumentAction, { action: Kind }>, 'action'>
    >;
};

export const DOCUMENT_ACTION_PARAMETERS = {
    search: ['query'],
    ask: ['query'],
    select: ['slug'],
} satisfies DocumentActionParameters;

/** Check whether a value is a supported scope for a search action. */
export function isDocumentSearchScope(
    value: unknown
): value is NonNullable<Extract<api.DocumentAction, { action: 'search' }>['scope']> {
    return value === 'site' || value === 'space';
}

/** Check whether a value is a supported inline button action kind. */
export function isDocumentButtonAction(value: unknown): value is api.DocumentAction['action'] {
    return typeof value === 'string' && value in DOCUMENT_ACTION_PARAMETERS;
}

/**
 * Create an inline button node pointing at a content ref or an action.
 */
export function createInlineButton(
    input: (
        | {
              ref: api.ContentRef;
          }
        | {
              action: api.DocumentAction;
          }
    ) & {
        label?: string;
        kind?: InlineButtonData['kind'];
        size?: InlineButtonData['size'];
        icon?: string;
    }
) {
    const { label = 'New button', kind = 'primary', size, icon } = input;
    const data: InlineButtonData = {
        label,
        kind,
        // Default size is the absent value; never persist it.
        ...(size && size !== DEFAULT_INLINE_BUTTON_SIZE ? { size } : {}),
        ...(icon ? { icon } : {}),
        ...('ref' in input ? { ref: input.ref } : { action: input.action }),
    };

    return Inline.create({
        type: INLINE_BUTTON,
        isVoid: true,
        data,
    });
}
